/**
 * Every number the rules use (docs/spec/02, 03), in one place. Distances are virtual pixels, times
 * are ticks (60 a second), speeds are pixels a tick, angles are radians.
 */

import type { CapsuleKind } from './types';

export const TICKS_PER_SECOND = 60;

export const FIELD = {
  w: 224,
  h: 288,
  /** The fighter's row. */
  fighterY: 252,
} as const;

export const FIGHTER = {
  speed: 2,
  /** Half the hit box. */
  hitW: 5,
  hitH: 5,
  /** A dual fighter's ships sit this far either side of `player.x`. */
  dualOffset: 8,
  /** The ship's centre never comes nearer the edge than this. */
  margin: 9,
  shotSpeed: 8,
  maxVolleys: 2,
  startFighters: 3,
  /** After a death: the least and the most the game waits for the divers to go home. */
  deathWait: 120,
  deathWaitMax: 360,
  readyTicks: 90,
} as const;

export const SHOT = { halfW: 1, halfH: 4 } as const;
export const BOMB = { halfW: 1, halfH: 3, maxAngle: 0.45, firstMin: 24, firstMax: 48, gap: 18, lowestY: 190 } as const;

/** Half hit boxes by kind (the Monarch is a little bigger). */
export const ENEMY_HIT = { small: 6, monarch: 7 } as const;

export const FORMATION = {
  cx: 112,
  top: 40,
  cell: 16,
  swayAmp: 16,
  swayPeriod: 240,
  breatheAmp: 0.25,
  breathePeriod: 160,
  /** Wing beat, ticks per frame. */
  beat: 32,
  /** Slots by row: [first column, last column]. */
  rows: [[3, 6], [1, 8], [1, 8], [0, 9], [0, 9]] as const,
} as const;

export const ENTRY = {
  /** Path distance between enemies in a stream. */
  spacing: 16,
  /** After the wave before is off its paths. */
  waveGap: 20,
  waveWaitMax: 300,
  homeTurn: 0.14,
  /** Within this of its slot an enemy flies straight in. */
  homeSnap: 24,
  /** Two-file entries fly this far either side of the path. */
  fileOffset: 8,
} as const;

export const DIVE = {
  peelRadius: 14,
  frenzyAt: 5,
  frenzySpeed: 1.15,
  /** One Monarch launch in this many is a beam run, when one is allowed. */
  beamOneIn: 2,
  /** One Stinger launch in this many becomes a morph (stage 4 on). */
  morphOneIn: 6,
  morphFrom: 4,
  morphFlash: 40,
  /** Path distance between the three of a trio. */
  trioGap: 16,
} as const;

export const BEAM = {
  /** The Monarch hovers this far above the fighter's row. */
  above: 88,
  minX: 40,
  maxX: 184,
  extend: 40,
  hold: 120,
  retract: 40,
  length: 80,
  topHalf: 6,
  bottomHalf: 24,
  /** Ticks the caught fighter takes to rise. */
  rise: 90,
  capturedMsg: 180,
  rescueSpin: 60,
  rescueSpeed: 2,
  /** A rescued ship that has not docked by now docks where it is (a fighter dodging it for ever). */
  rescueFlyMax: 240,
} as const;

export const CHALLENGE = {
  waveGap: 30,
  hit: 100,
  /** By challenging stage 1, 2, 3, then the last for ever. */
  waveBonus: [1000, 1500, 2000, 3000] as const,
  perfect: 10000,
  tallyTicks: 240,
  speed: 3.2,
} as const;

export const SCORE = {
  stinger: [50, 100],
  moth: [80, 160],
  /** Home; diving alone, with one escort, with two. */
  monarch: [150, 400, 800, 1600],
  captive: [500, 1000],
  morph: 160,
  morphBonus: [1000, 2000, 3000],
  /** Bonus fighters at these scores, then every `lifeEvery`. */
  lifeAt: [20000, 70000],
  lifeEvery: 70000,
  maxLives: 8,
} as const;

export const FLOW = {
  firstIntro: 200,
  intro: 100,
  challengeIntro: 150,
  warning: 180,
  clear: 90,
  /** The state stops changing this long after the game is over. */
  gameOverTicks: 240,
} as const;

/** How hard a stage is (02 §5). */
export interface Difficulty {
  readonly diveSpeed: number;
  readonly entrySpeed: number;
  readonly bombSpeed: number;
  readonly launchEvery: number;
  readonly maxGroups: number;
  readonly bombsPerDiver: number;
  /** Entering enemies may drop a bomb. */
  readonly entryFire: boolean;
  /** Extras brought by waves 3, 4 and 5. */
  readonly extras: number;
}

export function difficulty(stage: number): Difficulty {
  const n = Math.max(0, stage - 1);
  return {
    diveSpeed: Math.min(3.4, 2.2 + 0.06 * n),
    entrySpeed: Math.min(4.6, 3.6 + 0.04 * n),
    bombSpeed: Math.min(3.4, 2 + 0.07 * n),
    launchEvery: Math.max(34, 100 - 6 * n),
    maxGroups: stage >= 13 ? 5 : stage >= 7 ? 4 : stage >= 3 ? 3 : 2,
    bombsPerDiver: stage >= 10 ? 3 : stage >= 3 ? 2 : 1,
    entryFire: stage >= 2,
    extras: stage >= 9 ? 4 : stage >= 4 ? 2 : 0,
  };
}

/**
 * The stage whose difficulty a stage plays at. Supernova arms the fighter, so its hive hardens a
 * third faster (03 §1): stage 3 plays as 4, stage 9 as 12.
 */
export function heat(mode: 'classic' | 'supernova', stage: number): number {
  return mode === 'supernova' ? stage + Math.floor(stage / 3) : stage;
}

/** Supernova (03). */
export const NOVA = {
  capsuleSpeed: 1,
  /** Points for taking a capsule, never multiplied. */
  capsulePoints: 100,
  capsuleSway: 6,
  capsuleHalf: 7,
  dropAt: 20,
  timed: 600,
  chronoTicks: 360,
  rapidVolleys: 4,
  rapidShot: 1.25,
  rapidEvery: 7,
  spreadAngle: 0.26,
  safeTicks: 60,
  maxNovas: 3,
  startNovas: 1,
  novaEmpressDamage: 5,
  chainClock: 120,
  /** The chain length at which the multiplier becomes 2, 3, 4, 5. */
  chainTiers: [6, 14, 24, 36] as const,
  /** Supernova's scores run higher, so its bonus fighters come later: at these, then every `lifeEvery`. */
  lifeAt: [40000, 150000] as const,
  lifeEvery: 150000,
  empressEvery: 6,
  empressHp: 30,
  empressHpPerRank: 15,
  empressHalfW: 22,
  empressHalfH: 14,
  empressY: 64,
  empressSwing: 64,
  empressEnter: 120,
  attackGap: [90, 60] as const,
  fanBombs: [5, 7] as const,
  fanSpread: 1.2,
  fanVolleys: 3,
  fanGap: 30,
  swarm: [4, 6] as const,
  swarmGap: 20,
  lanceWarn: 50,
  lanceFire: 40,
  lanceHalf: 8,
  empressPoints: 10000,
  empressDying: 150,
} as const;

/** Capsule kinds and their weights in the draw (03 §2). */
export const CAPSULE_WEIGHTS: readonly (readonly [CapsuleKind, number])[] = [
  ['rapid', 5], ['spread', 5], ['pierce', 4], ['shield', 4], ['chrono', 3], ['nova', 3], ['life', 1],
];

/** The 1UP capsule appears only from this stage. */
export const LIFE_CAPSULE_FROM = 4;

/** The chain multiplier for a chain of `count` kills. */
export function chainMultiplier(count: number): number {
  let m = 1;
  for (const tier of NOVA.chainTiers) if (count >= tier) m++;
  return m;
}
